import type { ComponentProps } from 'react';
import type { Badge } from '@aaimplatform/ui';
import type { Integration } from '../../api/client';

/**
 * One badge per Integration.status value in platform-admin.openapi.yaml.
 *
 * Keyed by the generated union, so a status added to the contract is a type
 * error here until it has a label and tone. CONFIGURED is not red: red is
 * reserved for calls to action and alerts, and a configured integration is
 * neither. It reads as pending, since its credentials are set but a connection
 * has not been verified.
 *
 * `variant` is the Badge variant, the primitive the operator pages' shared
 * status maps compose (utils/status-labels.tsx), which prints the label in
 * sentence case. Badge is the one status chip (UI run r3,
 * status-chip-primitive), so `tone` is typed to the same Badge variant union
 * and carries the same value; it stays while a test still reads it.
 */
export const INTEGRATION_STATUS_BADGE: Record<
  Integration['status'],
  {
    tone: NonNullable<ComponentProps<typeof Badge>['variant']>;
    variant: NonNullable<ComponentProps<typeof Badge>['variant']>;
    label: string;
  }
> = {
  CONNECTED: { tone: 'success', variant: 'success', label: 'Connected' },
  CONFIGURED: { tone: 'warning', variant: 'warning', label: 'Configured' },
  ERROR: { tone: 'error', variant: 'error', label: 'Error' },
  NOT_CONFIGURED: { tone: 'neutral', variant: 'neutral', label: 'Not configured' },
};
